import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, unwrap } from './client';

const key = ['me', 'push'] as const;

export function usePushConfig() {
  return useQuery({ queryKey: key, queryFn: () => unwrap(api.GET('/api/v1/me/push')) });
}

export const pushSupported = () =>
  typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

function toUint8(base64url: string) {
  const padding = '='.repeat((4 - (base64url.length % 4)) % 4);
  const raw = atob((base64url + padding).replace(/-/g, '+').replace(/_/g, '/'));
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
}

async function currentSubscription() {
  const registration = await navigator.serviceWorker.ready;
  return registration.pushManager.getSubscription();
}

export function useBrowserPushState() {
  return useQuery({
    queryKey: [...key, 'browser'],
    queryFn: async () => (pushSupported() ? !!(await currentSubscription()) : false),
  });
}

/** Push auf diesem Gerät aktivieren (NOT-03). */
export function useEnablePush() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (publicKey: string) => {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') throw new Error('permission-denied');
      const registration = await navigator.serviceWorker.ready;
      const subscription =
        (await registration.pushManager.getSubscription()) ??
        (await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: toUint8(publicKey) }));
      const json = subscription.toJSON();
      await unwrap(
        api.POST('/api/v1/me/push-subscriptions', {
          body: { endpoint: json.endpoint!, keys: { p256dh: json.keys!.p256dh!, auth: json.keys!.auth! } },
        }),
      );
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: key }),
  });
}

export function useDisablePush() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const subscription = await currentSubscription();
      if (subscription) {
        await unwrap(api.DELETE('/api/v1/me/push-subscriptions', { params: { query: { endpoint: subscription.endpoint } } }));
        await subscription.unsubscribe();
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: key }),
  });
}

export function useTestPush() {
  return useMutation({ mutationFn: () => unwrap(api.POST('/api/v1/me/push-subscriptions/test')) });
}
